import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { DataProviderInstance } from '../api/DataProvider';

// Contexto do estado de origem de envio do WhatsApp (número da Gabriela x número próprio da
// clínica). Carrega `whatsapp/get_sender_status.php` uma vez ao montar (via DataProvider) e
// expõe o shape documentado em `docs/features/whatsapp-shared-number/api-contract.md` (#4) e
// `docs/features/whatsapp-shared-number/plan.md` (2.7).
const WhatsAppSenderContext = createContext(null);

const DEFAULT_OWN = {
  configured: false,
  use_own_number: false,
  allow_platform_fallback: false,
  status: 'not_configured',
  checked_at: null,
  error: null,
  instance_id: null,
};

// Fail-open: todas as capabilities nascem `true`. Enquanto o status ainda não carregou, ou se a
// chamada falhar (success:false / erro de rede), a UI NUNCA deve bloquear uma ação por causa
// disso — o backend já recusa (403/409/422) por conta própria quando a ação não é permitida.
// Bloquear otimisticamente aqui só serviria para esconder uma feature válida por uma falha de
// rede temporária do endpoint de status.
//
// Exceção: `agenda_send` nasce `false` (fail-closed). Ela decide se a agenda envia o aviso pelo
// sistema em vez de abrir o WhatsApp Web; o Web sempre funciona, e um `true` indevido faria a
// tela pular o window.open do clique e depois receber 403 do backend, sem como voltar ao Web.
const OPEN_CAPABILITIES = {
  manual_chat: true,
  document: true,
  bulk: true,
  template_edit: true,
  confirmation_retry: true,
  engagement: true,
  agenda_send: false,
};

const SAFE_DEFAULT = {
  loading: false,
  error: null,
  status: null,
  state: null,
  source: null,
  reason: null,
  own: DEFAULT_OWN,
  platform: { enabled: true, available: true },
  policyMode: null,
  capabilities: OPEN_CAPABILITIES,
  isPlatform: false,
  isOwnUnavailable: false,
  refresh: () => {},
};

const buildContextValue = (status, loading, error, refresh) => {
  // Enquanto carrega ou em falha, devolve o shape fail-open (ver comentário acima).
  if (loading || !status || status.success === false) {
    return {
      ...SAFE_DEFAULT,
      loading,
      error,
      refresh,
    };
  }

  const own = status.own || {};
  const platform = status.platform || {};
  const capabilities = status.capabilities || {};
  const state = status.state ?? null;

  return {
    loading,
    error,
    status,
    state,
    source: status.source ?? null,
    reason: status.reason ?? null,
    own: {
      configured: own.configured ?? false,
      use_own_number: own.use_own_number ?? false,
      allow_platform_fallback: own.allow_platform_fallback ?? false,
      status: own.status ?? 'not_configured',
      checked_at: own.checked_at ?? null,
      error: own.error ?? null,
      instance_id: own.instance_id ?? null,
    },
    platform: {
      enabled: platform.enabled ?? false,
      available: platform.available ?? false,
    },
    policyMode: status.policy_mode ?? null,
    capabilities: {
      manual_chat: capabilities.manual_chat ?? false,
      document: capabilities.document ?? false,
      bulk: capabilities.bulk ?? false,
      template_edit: capabilities.template_edit ?? false,
      confirmation_retry: capabilities.confirmation_retry ?? false,
      engagement: capabilities.engagement ?? false,
      agenda_send: capabilities.agenda_send ?? false,
    },
    isPlatform: state === 'platform',
    isOwnUnavailable: state === 'own_unavailable',
    refresh,
  };
};

export const WhatsAppSenderProvider = ({ children }) => {
  const dataProvider = DataProviderInstance();
  const companyId = dataProvider?.session?.currentCompany;

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(() => {
    if (!dataProvider || !dataProvider.GetWhatsappSenderStatus || companyId == null) {
      return;
    }
    setLoading(true);
    dataProvider.GetWhatsappSenderStatus((result) => {
      if (result && result.success) {
        setStatus(result);
        setError(null);
      } else {
        setStatus(result || null);
        setError((result && result.error) || 'Erro ao carregar status do WhatsApp');
      }
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataProvider, companyId]);

  useEffect(() => {
    if (companyId == null) {
      return;
    }
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const refresh = useCallback(() => {
    fetchStatus();
  }, [fetchStatus]);

  const value = buildContextValue(status, loading, error, refresh);

  return (
    <WhatsAppSenderContext.Provider value={value}>
      {children}
    </WhatsAppSenderContext.Provider>
  );
};

// Hook seguro: retorna o mesmo shape com defaults fail-open quando usado fora do
// WhatsAppSenderProvider (ex.: popups montados fora da árvore do DataProvider), para nunca
// quebrar a UI por falta de contexto.
export const useWhatsAppSenderSafe = () => {
  const context = useContext(WhatsAppSenderContext);
  if (!context) {
    return SAFE_DEFAULT;
  }
  return context;
};

export const useWhatsAppSender = useWhatsAppSenderSafe;

export default WhatsAppSenderContext;
